// ============================================================================
// MODULAR DESIGN PRINCIPLES (MANDATORY)
// ============================================================================
// This file strictly follows:
// 1. Single Responsibility Principle - Each component does ONE thing
// 2. Separation of Concerns - Logic separated from presentation
// 3. Modular Architecture - Independent, reusable components
//
// Refer to: docs/architecture/modular-design-standards.md
// ============================================================================

// Refer to docs/planning/ROLE_PERMISSION_MANAGEMENT_PLAN.md
/**
 * Role Editor Dialog
 * 
 * Dialog for creating or editing role details.
 */

import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  CircularProgress,
  Alert,
  Box
} from '@mui/material';
import { Save as SaveIcon, Cancel as CancelIcon } from '@mui/icons-material';

const RoleEditorDialog = ({ open, role, onClose, onSave }) => {
  const [formData, setFormData] = useState({
    name: '',
    code: '',
    description: ''
  });
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);

  // Reset form when dialog opens/closes or role changes
  useEffect(() => {
    if (open) {
      if (role) {
        // Edit mode
        setFormData({
          name: role.name || '',
          code: role.code || '',
          description: role.description || ''
        });
      } else {
        // Create mode
        setFormData({
          name: '',
          code: '',
          description: ''
        });
      }
      setErrors({});
    }
  }, [open, role]);

  // Auto-generate code from name
  const handleNameChange = (e) => {
    const name = e.target.value;
    setFormData(prev => ({
      ...prev,
      name,
      code: name.toUpperCase().replace(/\s+/g, '_').replace(/[^A-Z0-9_]/g, '')
    }));
    
    if (errors.name) {
      setErrors(prev => ({ ...prev, name: null }));
    }
  };

  const handleChange = (field) => (e) => {
    setFormData(prev => ({ ...prev, [field]: e.target.value }));
    
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: null }));
    }
  };

  const validate = () => {
    const newErrors = {};
    
    if (!formData.name || formData.name.trim().length === 0) {
      newErrors.name = 'Role name is required';
    }
    
    if (!formData.code || formData.code.trim().length === 0) {
      newErrors.code = 'Role code is required';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validate()) {
      return;
    }

    setSaving(true);
    
    try {
      await onSave(formData);
      handleClose();
    } catch (err) {
      setErrors({
        submit: err.response?.data?.detail || 'Failed to save role'
      });
    } finally {
      setSaving(false);
    }
  };

  const handleClose = () => {
    setFormData({ name: '', code: '', description: '' });
    setErrors({});
    setSaving(false);
    onClose();
  };

  return (
    <Dialog 
      open={open} 
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle>
        {role ? 'Edit Role' : 'Create New Role'}
      </DialogTitle>
      
      <DialogContent>
        {errors.submit && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {errors.submit}
          </Alert>
        )}

        <Box sx={{ pt: 1 }}>
          <TextField
            fullWidth
            label="Role Name"
            value={formData.name}
            onChange={handleNameChange}
            error={!!errors.name}
            helperText={errors.name || 'e.g., Senior Manager, Team Lead'}
            margin="normal"
            required
            disabled={saving || (role && role.is_system_role)}
          />

          <TextField
            fullWidth
            label="Role Code"
            value={formData.code}
            onChange={handleChange('code')}
            error={!!errors.code}
            helperText={errors.code || 'Auto-generated from name (e.g., SENIOR_MANAGER)'}
            margin="normal"
            required
            disabled={saving || !!role}
            InputProps={{
              readOnly: !!role
            }}
          />

          <TextField
            fullWidth
            label="Description"
            value={formData.description}
            onChange={handleChange('description')}
            multiline
            rows={3}
            margin="normal"
            helperText="Optional description of the role"
            disabled={saving}
          />
        </Box>
      </DialogContent>

      <DialogActions sx={{ p: 2 }}>
        <Button
          onClick={handleClose}
          disabled={saving}
          startIcon={<CancelIcon />}
        >
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={saving}
          startIcon={saving ? <CircularProgress size={20} /> : <SaveIcon />}
        >
          {saving ? 'Saving...' : role ? 'Update Role' : 'Create Role'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default RoleEditorDialog;
